import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  getMemoryActiveSubject,
  persistActiveSubject,
  readPersistedActiveSubject,
} from '../utils/activeSubjectStore';
import {
  coerceActiveSubjectToLoaded,
  listPickerSubjectCodes,
  type LoadedSubjectModules,
  type OptionalSubjectModuleCode,
  type SubjectCode,
} from '../utils/subjectConfig';
import {
  getMemoryLoadedSubjectModules,
  persistLoadedSubjectModules,
  readPersistedLoadedSubjectModules,
} from '../utils/subjectModuleStore';

type ActiveSubjectContextValue = {
  activeSubject: SubjectCode;
  setActiveSubject: (subject: SubjectCode) => void;
  loadedModules: LoadedSubjectModules;
  pickerSubjects: SubjectCode[];
  setSubjectModuleLoaded: (code: OptionalSubjectModuleCode, loaded: boolean) => void;
  hydrated: boolean;
};

function readInitialActiveSubject(): SubjectCode {
  if (typeof window === 'undefined') {
    return coerceActiveSubjectToLoaded(getMemoryActiveSubject(), getMemoryLoadedSubjectModules());
  }
  const loaded = readPersistedLoadedSubjectModules();
  return coerceActiveSubjectToLoaded(readPersistedActiveSubject(), loaded);
}

const ActiveSubjectContext = createContext<ActiveSubjectContextValue>({
  activeSubject: getMemoryActiveSubject(),
  setActiveSubject: () => {},
  loadedModules: getMemoryLoadedSubjectModules(),
  pickerSubjects: listPickerSubjectCodes(getMemoryLoadedSubjectModules()),
  setSubjectModuleLoaded: () => {},
  hydrated: false,
});

export function ActiveSubjectProvider({ children }: { children: ReactNode }) {
  const [loadedModules, setLoadedModules] = useState<LoadedSubjectModules>(() => (
    typeof window === 'undefined' ? getMemoryLoadedSubjectModules() : readPersistedLoadedSubjectModules()
  ));
  const [activeSubject, setActiveSubjectState] = useState<SubjectCode>(readInitialActiveSubject);
  const [hydrated, setHydrated] = useState(() => typeof window !== 'undefined');

  useEffect(() => {
    const nextModules = readPersistedLoadedSubjectModules();
    const nextSubject = coerceActiveSubjectToLoaded(readPersistedActiveSubject(), nextModules);
    if (nextSubject !== readPersistedActiveSubject()) {
      persistActiveSubject(nextSubject);
    }
    setLoadedModules(nextModules);
    setActiveSubjectState(nextSubject);
    setHydrated(true);
  }, []);

  const setActiveSubject = useCallback((subject: SubjectCode) => {
    setLoadedModules((currentModules) => {
      const nextSubject = coerceActiveSubjectToLoaded(subject, currentModules);
      persistActiveSubject(nextSubject);
      setActiveSubjectState(nextSubject);
      return currentModules;
    });
  }, []);

  const setSubjectModuleLoaded = useCallback((code: OptionalSubjectModuleCode, loaded: boolean) => {
    setLoadedModules((current) => {
      const next = { ...current, [code]: loaded };
      persistLoadedSubjectModules(next);
      setActiveSubjectState((active) => {
        const coerced = coerceActiveSubjectToLoaded(active, next);
        if (coerced !== active) {
          persistActiveSubject(coerced);
        }
        return coerced;
      });
      return next;
    });
  }, []);

  const pickerSubjects = useMemo(() => listPickerSubjectCodes(loadedModules), [loadedModules]);

  const value = useMemo(
    () => ({
      activeSubject,
      setActiveSubject,
      loadedModules,
      pickerSubjects,
      setSubjectModuleLoaded,
      hydrated,
    }),
    [activeSubject, hydrated, loadedModules, pickerSubjects, setActiveSubject, setSubjectModuleLoaded],
  );

  return (
    <ActiveSubjectContext.Provider value={value}>
      {children}
    </ActiveSubjectContext.Provider>
  );
}

export function useActiveSubject() {
  return useContext(ActiveSubjectContext);
}
